import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  input,
  viewChild,
} from '@angular/core';

import { RequiredInputParameterException } from '@web/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isNil, Nillable } from '@web/utils';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SummernoteImageUploadFn } from '../../types/summernote-image-upload-fn';
import {
  AlertsComponent,
  AlertsService,
  UploadException,
} from '@web/shared/ui';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let $: any;

@Component({
  selector: 'app-summernote',
  imports: [AlertsComponent],
  templateUrl: './summernote.component.html',
  styleUrl: './summernote.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SummernoteComponent),
      multi: true,
    },
    AlertsService,
  ],
})
export class SummernoteComponent
  implements AfterViewInit, ControlValueAccessor
{
  private readonly alerts = inject(AlertsService);

  public static instances = 0;

  public readonly uploadFn = input<SummernoteImageUploadFn>();
  public readonly height = input<number>(300);

  protected readonly summernoteId: string;

  private readonly editorRef = viewChild.required<ElementRef<HTMLElement>>('editor');

  private readonly disabledSource$ = new BehaviorSubject<boolean>(false);
  private readonly valueSource$ = new BehaviorSubject<Nillable<string>>(null);
  private readonly destroyRef = inject(DestroyRef);

  private isInitial = true;
  private onChange!: (value: Nillable<string>) => void;
  private onTouched!: () => void;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private summernoteRef: any;

  public constructor() {
    SummernoteComponent.instances++;

    this.summernoteId = this.configureSummernoteId();
  }

  public writeValue(value: Nillable<string>): void {
    this.valueSource$.next(value);
  }

  public registerOnChange(fn: (value: Nillable<string>) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabledSource$.next(isDisabled);
  }

  public ngAfterViewInit(): void {
    const insertOptions = ['link', 'picture', 'video'].filter((option) => {
      if (option === 'picture') {
        return !isNil(this.uploadFn());
      }

      return true;
    });

    // Inicjalizacja po referencji, a nie po id - element może nie być jeszcze podpięty do dokumentu (np. w zwiniętym panelu akordeonu)
    this.summernoteRef = $(this.editorRef().nativeElement).summernote({
      height: this.height(),
      lang: 'pl-PL',
      toolbar: [
        ['style', ['style']],
        ['font', ['bold', 'underline', 'clear']],
        ['fontname', ['fontname']],
        ['color', ['color']],
        ['para', ['ul', 'ol', 'paragraph']],
        ['table', ['table']],
        ['insert', insertOptions],
        ['view', ['codeview', 'help']],
      ],
      callbacks: {
        onFocus: () => this.onTouched(),
        onChange: (contents: string) => {
          // Pomijam zmianę wynikającą z inicjalizacji komponentu wartością
          if (this.isInitial) {
            this.isInitial = false;

            return;
          }

          // Jeśli treść pochodząca z edytora jest <br>, to oznacza, że edytor nie jest wypełniony treścią.
          if (contents === '<br>') {
            contents = '';
          }

          this.onChange(contents);
        },
        onImageUpload: (files: FileList) => this.onImageUpload(files),
        onBlurCodeview: (contents: string) => this.onChange(contents),
      },
    });

    this.listenToDisabledChange();
    this.listenToValueChange();
  }

  private listenToDisabledChange(): void {
    this.disabledSource$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((disabled) =>
        disabled ? this.summernoteDisable() : this.summernoteEnabled(),
      );
  }

  private listenToValueChange(): void {
    this.valueSource$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.fillSummernoteByValue(value);
      });
  }

  private fillSummernoteByValue(value: Nillable<string>): void {
    this.summernoteRef.summernote('code', value);
  }

  private summernoteEnabled(): void {
    this.summernoteRef.summernote('enable');
  }

  private summernoteDisable(): void {
    this.summernoteRef.summernote('disable');
  }

  private onImageUpload(files: FileList): void {
    const uploadFn = this.uploadFn();

    if (isNil(uploadFn)) {
      throw new RequiredInputParameterException(this.uploadFn.name);
    }

    uploadFn(files)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (images) => {
          images.forEach((image) => {
            this.summernoteRef.summernote(
              'insertImage',
              image.url,
              image.title,
            );
          });
        },
        error: (error) => {
          if (UploadException.isInstance(UploadException)) {
            const { message } = error;

            this.showFailureMessage(message);

            return;
          }

          this.showFailureMessage(
            'Wystąpił nieznany błąd podczas wgrywania pliku. Skontaktuj się z administratorem.',
          );
        },
      });
  }

  private showFailureMessage(message: string): void {
    this.alerts.showSingle((builder) => builder.initFailure(message).build());
  }

  private configureSummernoteId(): string {
    return `summernote_${SummernoteComponent.instances}`;
  }
}
